import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchGalleryByEvents } from '@/services/api';
import ImageModal from '@/components/ImageModal';
import SeoHead from '@/components/Seo';
import { ChevronLeft, Maximize2, Calendar, Share2 } from 'lucide-react';

interface ImageItem {
    id: number;
    source: string;
    caption: string | null;
    title: string;
    created_at: string;
}

const AlbumPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [images, setImages] = useState<ImageItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState('');
    const [date, setDate] = useState('');
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [ogImage, setOgImage] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;

        const fetchAlbum = async () => {
            try {
                const response = await fetchGalleryByEvents(id!);
                const data = response.data;
                setImages(data.images || []);
                setTitle(data.title || 'Gallery');
                setDate(data.created_at || '');
                
                if (data.images && data.images.length > 0) {
                    setOgImage(data.images[0].source);
                }
            } catch (err) {
                console.error('Failed to fetch album', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAlbum();
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-slate-400 font-serif italic">Opening Archives...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#fcfaf7] min-h-screen pb-24">
            <SeoHead
                title={title}
                description={`${title} — Photo highlights from the Diocese of Ibadan South.`}
                ogImage={ogImage}
            />

            {/* Floating Top Navigation */}
            <nav className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-100 px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <button 
                        onClick={() => navigate('/gallery')}
                        className="flex items-center gap-2 text-slate-500 hover:text-amber-600 transition-colors group"
                    >
                        <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Back to Gallery</span>
                    </button>
                    <div className="flex items-center gap-4">
                        <button className="p-2 text-slate-400 hover:text-slate-900 transition-colors">
                            <Share2 size={18} />
                        </button>
                    </div>
                </div>
            </nav>

            <header className="max-w-7xl mx-auto px-6 pt-16 pb-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4"
                >
                    <div className="flex items-center gap-3 text-amber-600">
                        <Calendar size={16} />
                        <span className="text-[10px] font-bold uppercase tracking-[0.3em]">
                            {date ? new Date(date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Event Archive'}
                        </span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 leading-tight">
                        {title}
                    </h1>
                    <div className="h-1 w-20 bg-amber-500 rounded-full" />
                </motion.div>
            </header>

            <main className="max-w-7xl mx-auto px-6">
                {images.length === 0 ? (
                    <div className="py-24 text-center border-2 border-dashed border-slate-200 rounded-[3rem]">
                        <p className="text-slate-400 font-serif italic">This album is currently empty.</p>
                    </div>
                ) : (
                    <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
                        {images.map((img, index) => (
                            <motion.div
                                key={img.id}
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: (index % 10) * 0.05 }}
                                className="relative group cursor-zoom-in break-inside-avoid"
                                onClick={() => setSelectedImage(img.source)}
                            >
                                <div className="relative overflow-hidden rounded-2xl bg-slate-200 shadow-lg shadow-slate-200/50">
                                    <img
                                        src={img.source}
                                        alt={img.caption || img.title}
                                        className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                                        loading="lazy"
                                    />
                                    
                                    {/* Hover Overlay */}
                                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                        <div className="bg-white/20 backdrop-blur-md p-3 rounded-full text-white">
                                            <Maximize2 size={20} />
                                        </div>
                                    </div>
                                </div>

                                {img.caption && (
                                    <div className="mt-3 px-2">
                                        <p className="text-sm font-serif italic text-slate-500 leading-relaxed">
                                            {img.caption}
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}
            </main>

            {/* Modal - Ensure your ImageModal is responsive */}
            <AnimatePresence>
                {selectedImage && (
                    <ImageModal 
                        imageUrl={selectedImage} 
                        onClose={() => setSelectedImage(null)} 
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default AlbumPage;